import { css } from "@emotion/css";
import type { Vec3 } from "eutopia.ts";
import {
  $,
  effect,
  onCleanup,
  type Properties,
  resolve,
  signal,
} from "signlets";

import { metersPerNauticalMile } from "../constants";
import { normalizeDegrees, trueToMagneticDegrees } from "../math";
import type { Vehicle } from "../vehicle";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { classes } from "./classes";
import { DirectionInput } from "./DirectionInput";
import { Field } from "./Field";
import { H1 } from "./H1";
import { H2 } from "./H2";
import { CloseIcon } from "./icons/CloseIcon";
import { LoadingButton } from "./LoadingButton";
import { Measurement } from "./Measurement";
import { Modal } from "./Modal";
import { P } from "./P";

const style = css`
  width: 32rem;

  > .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  > .steps {
    display: grid;
    min-height: 14rem;

    > .step {
      grid-area: 1 / 1;
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      opacity: 0;
      transition: opacity 200ms;

      &.active {
        opacity: 1;
        transition-delay: 0ms;
      }

      > .content {
        height: 3rem;

        > .row {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
      }

      > button {
        width: max-content;
        min-width: 10rem;
      }

      > button:last-child {
        margin-top: auto;
      }
    }
  }
`;

export type PreflightProps = {
  visible: boolean;
  vehicle?: Vehicle;
  onClose: () => void;
};

const steps = [
  "introduction",
  "battery",
  "surfaces",
  "airspeed",
  "compass",
  "launch",
] as const;
type Step = (typeof steps)[number];

export const Preflight = ({
  visible,
  vehicle,
  onClose,
}: Properties<PreflightProps>) => {
  const [step, setStep] = signal<Step>("introduction");

  const currentStepIndex = $(() => steps.indexOf(step()));

  const onNext = () =>
    setStep(
      steps[Math.min(steps.length - 1, currentStepIndex() + 1)] ??
        "introduction",
    );

  effect(() => {
    if (!resolve(visible)) {
      setStep("introduction");
      return;
    }

    const controller = new AbortController();
    window.addEventListener(
      "keydown",
      ({ key }) => {
        if (key === "Escape") onClose();
      },
      { signal: controller.signal },
    );
    onCleanup(() => controller.abort());
  });

  return (
    <Modal class={style} visible={visible}>
      <div class="title">
        <H1>
          {$(() => (step() === "launch" ? "Ready" : "Preflight checklist"))}
        </H1>
        <Button icon title="Close" onClick={onClose}>
          <CloseIcon />
        </Button>
      </div>
      <div class="steps">
        <Introduction step={step} onNext={onNext} />
        <Battery step={step} vehicle={vehicle} onNext={onNext} />
        <Surfaces step={step} vehicle={vehicle} onNext={onNext} />
        <Airspeed step={step} vehicle={vehicle} onNext={onNext} />
        <Compass step={step} vehicle={vehicle} onNext={onNext} />
        <Launch step={step} onClose={onClose} />
      </div>
    </Modal>
  );
};

type StepProps = {
  step: Step;
  value: Step;
  children?: JSX.Element;
};

const Step = ({ step, value, children }: Properties<StepProps>) => (
  <section
    class={$(() => classes("step", resolve(step) === value && "active"))}
    inert={$(() => resolve(step) !== value)}
  >
    {children}
  </section>
);

type IntroductionProps = {
  step: Step;
  onNext: () => void;
};

const Introduction = ({ step, onNext }: Properties<IntroductionProps>) => (
  <Step step={step} value="introduction">
    <P>Complete all checks before proceeding.</P>
    <Button primary onClick={onNext}>
      Next
    </Button>
  </Step>
);

type CheckStepProps = {
  step: Step;
  vehicle?: Vehicle;
  onNext: () => void;
};

const Battery = ({ step, vehicle, onNext }: Properties<CheckStepProps>) => {
  const [checked, setChecked] = signal(false);

  effect(() => {
    if (resolve(step) !== "battery") setChecked(false);
  });

  return (
    <Step step={step} value="battery">
      <H2>1. Battery check</H2>
      <div class="content">
        <Measurement
          label="Battery voltage"
          value={$(() =>
            (resolve(vehicle)?.state().batteryVoltage ?? 0).toFixed(1),
          )}
          unit="V"
        />
      </div>
      <Checkbox value={checked} onChange={setChecked}>
        Battery voltage is within safe operating range (&gt; 12.3 V)
      </Checkbox>
      <Button primary disabled={$(() => !checked())} onClick={onNext}>
        Next
      </Button>
    </Step>
  );
};

const Surfaces = ({ step, vehicle, onNext }: Properties<CheckStepProps>) => {
  const [checked, setChecked] = signal(false);

  const onTestSurfaces = async () => {
    await resolve(vehicle)?.surfacesTest();
  };

  effect(() => {
    if (resolve(step) !== "surfaces") setChecked(false);
  });

  return (
    <Step step={step} value="surfaces">
      <H2>2. Control surfaces check</H2>
      <div class="content">
        <LoadingButton onClick={onTestSurfaces}>Test surfaces</LoadingButton>
      </div>
      <Checkbox value={checked} onChange={setChecked}>
        Flight controls free and correct
      </Checkbox>
      <Button primary disabled={$(() => !checked())} onClick={onNext}>
        Next
      </Button>
    </Step>
  );
};

const Airspeed = ({ step, vehicle, onNext }: Properties<CheckStepProps>) => {
  const [checked, setChecked] = signal(false);

  effect(() => {
    if (resolve(step) !== "airspeed") setChecked(false);
  });

  return (
    <Step step={step} value="airspeed">
      <H2>3. Airspeed check</H2>
      <div class="content">
        <Measurement
          label="Airspeed"
          value={$(() =>
            (
              (resolve(vehicle)?.state().airspeed ?? 0) /
              (metersPerNauticalMile / 3600)
            ).toFixed(1),
          )}
          unit="kt"
        />
      </div>
      <Checkbox value={checked} onChange={setChecked}>
        Airspeed sensor responds to gentle blowing
      </Checkbox>
      <Button primary disabled={$(() => !checked())} onClick={onNext}>
        Next
      </Button>
    </Step>
  );
};

const Compass = ({ step, vehicle, onNext }: Properties<CheckStepProps>) => {
  const [checked, setChecked] = signal(false);
  const [calibrating, setCalibrating] = signal(false);
  const [value, setValue] = signal(0);

  effect(() => {
    if (resolve(step) === "compass") return;
    setChecked(false);
    setCalibrating(false);
  });

  const position = $(
    (): Vec3 => resolve(vehicle)?.state().position ?? [0, 0, 0],
  );
  const heading = $(() => resolve(vehicle)?.state().heading ?? 0);
  const current = $(() =>
    normalizeDegrees(Math.round(trueToMagneticDegrees(heading(), position()))),
  );

  const onCalibrate = async () => {
    if (calibrating()) {
      await resolve(vehicle)?.calibrateMagnetometer(value());
      setCalibrating(false);
      return;
    } else {
      setValue(heading());
      setCalibrating(true);
    }
  };

  return (
    <Step step={step} value="compass">
      <H2>4. Compass check</H2>
      <div class="content">
        <div class="row">
          {$(() =>
            resolve(calibrating) ? (
              <Field label="Heading (°)">
                <DirectionInput
                  position={position}
                  direction={value}
                  onChangeDirection={setValue}
                />
              </Field>
            ) : (
              <Measurement label="Heading" value={current} unit="°" />
            ),
          )}
          <LoadingButton onClick={onCalibrate}>Calibrate</LoadingButton>
        </div>
      </div>
      <Checkbox value={checked} onChange={setChecked}>
        Compass heading is accurate
      </Checkbox>
      <Button primary disabled={$(() => !checked())} onClick={onNext}>
        Next
      </Button>
    </Step>
  );
};

type LaunchProps = {
  step: Step;
  onClose: () => void;
};

const Launch = ({ step, onClose }: Properties<LaunchProps>) => (
  <Step step={step} value="launch">
    <P>Silent Arrow cleared for loading.</P>
    <Button primary onClick={onClose}>
      Proceed
    </Button>
  </Step>
);
